import { APIEndpoints, type Nullable } from '@assembly-web/services';
import type {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { assemblyAPI } from '../../../../../services/assemblyAPI';
import { CriteriaRuleType } from '../useShareCollectionsModal';
import type {
  ExcludeCriteriaType,
  IncludeCriteriaType,
} from './useSaveShareCollectionCriteria';

export type GetMembersForPreviewRequest = {
  criteriaType: 'simple';
  criteria: {
    include: IncludeCriteriaType[];
    exclude: ExcludeCriteriaType[];
  };
};

export type GetMembersFromCriteriaJobResponse = {
  jobId: string;
  previewId: string;
  createdAt: string;
};

export type GetMembersFromCriteriaResponse = {
  previewId: string;
  createdAt: string;
};

export type GetMembersFromCriteriaForPreviewResponse = {
  data: GetMembersFromCriteriaJobResponse | GetMembersFromCriteriaResponse;
};

export type PreviewMember = {
  name: string;
  email: string;
  image?: string;
  firstName: string;
  lastName?: string;
  memberId?: string;
  state?: 'ACTIVE' | 'NEW' | 'PENDING';
  meta?: {
    permission: string;
  };
};

export type GetMembersForPreviewResponse = {
  total: number;
  data: PreviewMember[];
  metadata: {
    pendingCount: number;
    pagination: { cursor: { previous: null; next: null } };
  };
};

export const usePreviewIDForCriteria = ({
  payload,
  enabled,
  collectionOwnerId,
}: {
  payload: GetMembersForPreviewRequest;
  enabled?: boolean;
  collectionOwnerId: string | null;
}) => {
  return useQuery<GetMembersFromCriteriaForPreviewResponse>(
    ['getCriteriaPreview', payload],
    () =>
      assemblyAPI.post(APIEndpoints.previewShareCriteria, {
        ...payload,
        for: 'collection',
        includes: [
          {
            type: CriteriaRuleType.Member.toUpperCase(),
            value: [collectionOwnerId ?? ''],
          },
        ],
      }),
    {
      cacheTime: 0,
      enabled,
    }
  );
};

export function getPreviewMembersForCollection(
  previewId: Nullable<string>,
  search: string | undefined,
  filter: string | undefined
): UseInfiniteQueryOptions<GetMembersForPreviewResponse> {
  return {
    enabled: Boolean(previewId),
    queryKey: ['getMembersCriteriaPreview', previewId, search, filter],
    queryFn: async ({ pageParam = '' }) => {
      const response = await assemblyAPI.get(
        APIEndpoints.previewMembersForShareCriteria(previewId ?? ''),
        {
          params: {
            search,
            filter,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }
      );
      return response.data;
    },
    staleTime: 60 * 5 * 1000,
    getNextPageParam: (page: GetMembersForPreviewResponse) =>
      page.metadata.pagination.cursor.next,
    getPreviousPageParam: (page: GetMembersForPreviewResponse) =>
      page.metadata.pagination.cursor.previous,
  };
}

export function useGetMembersPreview(
  previewId: Nullable<string>,
  previewMemberFilter: {
    search: string | undefined;
    filter: string | undefined;
  }
): UseInfiniteQueryResult<GetMembersForPreviewResponse> {
  return useInfiniteQuery(
    getPreviewMembersForCollection(
      previewId,
      previewMemberFilter.search,
      previewMemberFilter.filter === 'all'
        ? undefined
        : previewMemberFilter.filter
    )
  );
}
